import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { Refresh, OpenInNew } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { rebuildInvoicePDF } from "store/InvoiceSlice";

export default function RerenderInvoicePDFModal({ open, onClose, invoice }) {
  const dispatch = useDispatch();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setProcessing(false);
      setError(null);
      setPdfUrl(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!invoice?.id) {
      setError("Invoice ID is required.");
      return;
    }

    setProcessing(true);
    setError(null);
    setPdfUrl(null);

    try {
      const result = await dispatch(rebuildInvoicePDF({ id: invoice.id })).unwrap();
      
      if (result?.success === true && result?.pdfUrl) {
        setPdfUrl(result.pdfUrl);
      } else if (result?.success === true) {
        setError("PDF rebuilt successfully but no URL was returned.");
      } else {
        setError(result?.error || "Failed to rebuild PDF. Please try again.");
      }
    } catch (err) {
      const errorMessage = err || "Failed to rebuild PDF. Please try again.";
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="rerender-pdf-dialog-title"
      aria-describedby="rerender-pdf-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="rerender-pdf-dialog-title">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Refresh />
          <Typography variant="h3">Rebuild Invoice PDF</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {invoice && (
            <Typography variant="body1" paragraph>
              This will rebuild the PDF for invoice <strong>'{invoice.name || invoice.invoice_num || "Unnamed Invoice"}'</strong>.
            </Typography>
          )}

          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              What happens when you rebuild:
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>If you have changed your invoice settings (company information, payment details, terms, etc.), the rebuilt PDF will reflect those changes.</li>
                <li>The invoice will be regenerated with the latest settings and data.</li>
                <li>This process may take a few moments to complete.</li>
              </ul>
            </Typography>
          </Box>

          {processing && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                p: 3,
                backgroundColor: "action.hover",
                borderRadius: 1,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body1">Rebuilding PDF...</Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a few moments
              </Typography>
            </Box>
          )}

          {error && !processing && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {pdfUrl && !processing && (
            <Alert severity="success" icon={<OpenInNew />}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                PDF rebuilt successfully!
              </Typography>
              <Link
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                Open PDF
                <OpenInNew fontSize="small" />
              </Link>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={processing}>
          {pdfUrl ? "Close" : "Cancel"}
        </Button>
        {!pdfUrl && (
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            disabled={processing || !invoice?.id}
            startIcon={processing ? <CircularProgress size={16} /> : <Refresh />}
          >
            {processing ? "Rebuilding..." : "Rebuild PDF"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

